import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  Radio,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
  PiCaretDown,
  PiCaretRight,
  PiCheck,
  PiCopy,
  PiFileText,
  PiPaperclip,
  PiPencilSimple,
  PiTrash,
  PiX,
} from 'react-icons/pi';
import DocumentsUpload from '@/components/DocumentsUpload/DocumentsUpload';
import { FactRow, money } from '@/components/InvoiceDetailsModal/DocumentCard';
import { ApiError } from '@/lib/api';
import {
  type InvoiceDetails,
  type PaymentMovement,
  type TPaymentReport,
  useDownloadDocument,
  useLinkPaymentReport,
  useRemovePaymentReport,
  useReplacePaymentReportFile,
} from '@/models/invoices';
import { DocumentType } from '@/types';

/** The choice of no payment in the confirmation's payment list. */
const NONE = 'none';

const day = (v: string | null) => (v ? dayjs(v).format('DD.MM.YYYY') : '-');
const when = (v: string | null) =>
  v ? dayjs(v).format('DD.MM.YYYY HH:mm') : 'date unknown';

/** Who added a confirmation: the client, or the staff member who entered it. */
const sourceOf = (r: TPaymentReport) =>
  r.source === 'customer_invoice_link' || r.source === 'customer_offer_page'
    ? 'Client'
    : r.source === 'legacy_import'
      ? 'Before confirmations existed'
      : (r.reportedByName ?? r.reportedByEmail ?? 'Back office');

const paymentText = (p: PaymentMovement, currency: string) =>
  `+${money(p.amount, currency)} · ${day(p.collectedAt)}${
    p.senderName ? ` · ${p.senderName}` : ''
  }`;

/** A tooltip of several lines over a whole row. */
const Facts = ({ lines }: { lines: string[] }) => (
  <Stack gap={2}>
    {lines.map((l) => (
      <Text key={l} fz="xs" style={{ wordBreak: 'break-all' }}>
        {l}
      </Text>
    ))}
  </Stack>
);

/** The proof as text on one line, cut with "...": the file's name or the hash. */
function ProofText({ report }: { report: TPaymentReport }) {
  const removed = report.removedAt != null;
  return (
    <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
      {report.fileId != null && (
        <PiFileText size={12} style={{ flexShrink: 0 }} />
      )}
      <Text
        fz="xs"
        truncate="end"
        ff={report.fileId == null ? 'monospace' : undefined}
        td={removed ? 'line-through' : undefined}
        c={removed ? 'dimmed' : undefined}
      >
        {report.fileId != null
          ? (report.fileName ?? 'Statement')
          : (report.hash ?? '-')}
      </Text>
    </Group>
  );
}

/** The proof in full: the file to download, or the hash to copy. */
function ProofFull({ report }: { report: TPaymentReport }) {
  const download = useDownloadDocument();
  if (report.fileId != null) {
    return (
      <Anchor
        component="button"
        type="button"
        fz="xs"
        onClick={() => download.mutate(report.fileId!)}
        style={{ textAlign: 'left' }}
      >
        <Group gap={4} wrap="nowrap">
          <PiFileText size={12} style={{ flexShrink: 0 }} />
          <Text inherit style={{ wordBreak: 'break-all' }}>
            {report.fileName ?? 'Statement'}
          </Text>
        </Group>
      </Anchor>
    );
  }
  if (!report.hash) return <Text fz="xs">-</Text>;
  return (
    <Group gap={4} wrap="nowrap" align="flex-start">
      <Text fz="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
        {report.hash}
      </Text>
      <CopyButton value={report.hash}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied' : 'Copy hash'} fz="xs">
            <ActionIcon
              size="xs"
              variant="subtle"
              color={copied ? 'teal' : 'gray'}
              aria-label="Copy hash"
              onClick={copy}
            >
              {copied ? <PiCheck size={12} /> : <PiCopy size={12} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

/**
 * The proforma's money: the payments collected or returned, then the payment confirmations. A
 * confirmation always belongs to the proforma and sometimes to one of its payments; it is a claim to
 * check against the bank or the chain, never money received. Clicking a confirmation opens it: its
 * details, the payment it is for, and the way to replace or remove it. Removed ones stay on record,
 * folded away.
 */
export function PaymentLedger({
  invoice,
  canChange,
}: {
  invoice: InvoiceDetails;
  canChange: boolean;
}) {
  const payments = invoice.payments ?? [];
  const reports = invoice.paymentReports ?? [];
  const currency = invoice.dueCurrency;
  const remove = useRemovePaymentReport();
  const replace = useReplacePaymentReportFile();
  const link = useLinkPaymentReport();
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [removing, setRemoving] = useState<TPaymentReport | null>(null);
  // Correcting the proof of the viewed confirmation in place: null when not, else the hash typed.
  const [editing, setEditing] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const active = reports.filter((r) => r.removedAt == null);
  const removed = reports.filter((r) => r.removedAt != null);
  const reportOf = (paymentId: string) =>
    active.find((r) => r.linkedPaymentIds.includes(paymentId)) ?? null;
  // Collections with no confirmation in force: what a confirmation may be associated with.
  const free = payments.filter((p) => p.amount > 0 && !reportOf(p.id));
  const viewed = active.find((r) => r.id === viewingId) ?? null;
  const viewedPayments = viewed
    ? payments.filter((p) => viewed.linkedPaymentIds.includes(p.id))
    : [];
  // The payment the viewed confirmation is for now, what may be chosen instead, and the choice.
  const current = viewedPayments[0]?.id ?? NONE;
  const choices = [...viewedPayments, ...free];
  const chosen = picked ?? current;
  const busy = remove.isPending || replace.isPending;

  const open = (r: TPaymentReport) => {
    setPicked(null);
    setEditing(null);
    setViewingId(r.id);
  };
  const close = () => {
    if (busy) return;
    setRemoving(null);
    setReason('');
    setError(null);
  };
  const failed = (e: unknown, what: string) =>
    setError(
      e instanceof ApiError
        ? e.body || `The ${what} did not go through.`
        : 'We did not get a response. Check the payments before trying again.',
    );

  const submitAssociate = () => {
    if (!viewed || chosen === current) return;
    link.mutate(
      {
        invoiceId: invoice.id.toString(),
        paymentId: chosen === NONE ? null : chosen,
        reportId: viewed.id,
      },
      {
        onSuccess: () => setPicked(null),
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Not associated',
            message: e instanceof ApiError ? e.body : String(e),
          }),
      },
    );
  };
  const submitRemove = async () => {
    if (!removing || !reason.trim()) return;
    setError(null);
    try {
      await remove.mutateAsync({
        invoiceId: invoice.id.toString(),
        reportId: removing.id,
        reason: reason.trim(),
      });
      close();
    } catch (e) {
      failed(e, 'removal');
    }
  };
  // A correction needs no reason: the old proof stays on record, the dialog follows the new one.
  const saveProof = (proof: { fileId: number } | { hash: string }) => {
    if (!viewed) return;
    replace.mutate(
      { invoiceId: invoice.id.toString(), reportId: viewed.id, ...proof },
      {
        onSuccess: ({ reportId }) => {
          setEditing(null);
          setViewingId(reportId);
        },
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Not changed',
            message: e instanceof ApiError ? e.body : String(e),
          }),
      },
    );
  };
  if (payments.length === 0 && reports.length === 0) {
    return (
      <Text fz="xs" c="dimmed">
        Nothing recorded yet.
      </Text>
    );
  }

  return (
    <Stack gap={4}>
      {payments.length > 0 && (
        <Table fz="xs" verticalSpacing={4} horizontalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {payments.map((p) => {
              const report = reportOf(p.id);
              const facts = [
                `${p.amount < 0 ? 'Returned' : 'Collected'} ${money(Math.abs(p.amount), currency)} on ${day(p.collectedAt)}`,
                p.amount > 0
                  ? `From: ${p.senderName ?? 'unknown sender'}`
                  : null,
                `Recorded by ${p.recordedByEmail ?? 'system'}, ${when(p.recordedAt)}`,
                report
                  ? `Confirmation: ${report.fileName ?? report.hash ?? `#${report.id}`}`
                  : p.amount > 0
                    ? 'No payment confirmation'
                    : null,
              ].filter((x): x is string => x != null);
              return (
                <Tooltip
                  key={p.id}
                  label={<Facts lines={facts} />}
                  multiline
                  w={300}
                  openDelay={300}
                  position="left"
                >
                  <Table.Tr>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        {day(p.collectedAt)}
                        {report && (
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            aria-label="Open its payment confirmation"
                            onClick={() => open(report)}
                          >
                            <PiPaperclip size={12} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td
                      ta="right"
                      fw={600}
                      c={p.amount < 0 ? 'orange' : undefined}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {p.amount < 0 ? '-' : '+'}
                      {money(Math.abs(p.amount), currency)}
                    </Table.Td>
                  </Table.Tr>
                </Tooltip>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      {active.length > 0 && (
        <>
          <Text fz="xs" fw={600} mt={6}>
            Payment confirmations
          </Text>
          <Table
            fz="xs"
            verticalSpacing={4}
            horizontalSpacing={6}
            highlightOnHover
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Confirmation</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {active.map((r) => (
                <Table.Tr
                  key={r.id}
                  onClick={() => open(r)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    {day(r.reportedAt)}
                  </Table.Td>
                  <Table.Td style={{ maxWidth: 110 }}>
                    <Text fz="xs" truncate="end">
                      {sourceOf(r)}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ maxWidth: 150 }}>
                    <ProofText report={r} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}

      {removed.length > 0 && (
        <Stack gap={4}>
          <UnstyledButton onClick={() => setShowRemoved((v) => !v)}>
            <Group gap={4}>
              {showRemoved ? (
                <PiCaretDown size={12} />
              ) : (
                <PiCaretRight size={12} />
              )}
              <Text fz="xs" c="dimmed">
                Removed confirmations ({removed.length})
              </Text>
            </Group>
          </UnstyledButton>
          {showRemoved &&
            removed.map((r) => (
              <Stack key={r.id} gap={0} pl="md">
                <ProofText report={r} />
                <Text fz={11} c="dimmed">
                  {when(r.removedAt)}
                  {r.removedByEmail ? ` · ${r.removedByEmail}` : ''}:{' '}
                  {r.removedReason}
                </Text>
              </Stack>
            ))}
        </Stack>
      )}

      <Modal
        opened={viewed != null}
        onClose={() =>
          !link.isPending && !replace.isPending && setViewingId(null)
        }
        title="Payment confirmation"
        size="md"
        centered
      >
        {viewed && (
          <Stack gap="md">
            <Stack gap={6}>
              <FactRow label={viewed.fileId != null ? 'File' : 'Hash'}>
                {editing == null ? (
                  <Group gap={4} wrap="nowrap" align="flex-start">
                    <ProofFull report={viewed} />
                    {canChange && (viewed.fileId != null || viewed.hash) && (
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="gray"
                        aria-label={
                          viewed.fileId != null ? 'Change file' : 'Change hash'
                        }
                        onClick={() => setEditing(viewed.hash ?? '')}
                      >
                        <PiPencilSimple size={12} />
                      </ActionIcon>
                    )}
                  </Group>
                ) : viewed.fileId == null ? (
                  <Group gap={4} wrap="nowrap">
                    <TextInput
                      size="xs"
                      style={{ flex: 1 }}
                      value={editing}
                      onChange={(e) => setEditing(e.currentTarget.value)}
                      disabled={replace.isPending}
                      styles={{ input: { fontFamily: 'monospace' } }}
                      autoFocus
                    />
                    <ActionIcon
                      size="sm"
                      aria-label="Save hash"
                      loading={replace.isPending}
                      disabled={
                        !editing.trim() || editing.trim() === viewed.hash
                      }
                      onClick={() => saveProof({ hash: editing.trim() })}
                    >
                      <PiCheck size={12} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="gray"
                      aria-label="Cancel"
                      disabled={replace.isPending}
                      onClick={() => setEditing(null)}
                    >
                      <PiX size={12} />
                    </ActionIcon>
                  </Group>
                ) : (
                  <Stack gap={4}>
                    <DocumentsUpload
                      type={DocumentType.PaymentConfirmation}
                      pulseId={invoice.contactId}
                      maxDocuments={1}
                      onUploadSuccess={(documents) => {
                        if (documents.length > 0) {
                          saveProof({ fileId: Number(documents[0].id) });
                        }
                      }}
                    />
                    <Anchor
                      component="button"
                      type="button"
                      fz="xs"
                      c="dimmed"
                      onClick={() => setEditing(null)}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      Keep the file
                    </Anchor>
                  </Stack>
                )}
              </FactRow>
              <FactRow label="Added">{when(viewed.reportedAt)}</FactRow>
              <FactRow label="Source">{sourceOf(viewed)}</FactRow>
              {viewed.note && <FactRow label="Note">{viewed.note}</FactRow>}
              <FactRow label="Payment">
                {viewedPayments.length > 0 ? (
                  viewedPayments.map((p) => (
                    <Text key={p.id} fz="xs">
                      {paymentText(p, currency)}
                    </Text>
                  ))
                ) : (
                  <Text fz="xs" c="dimmed">
                    Not associated with a payment
                  </Text>
                )}
              </FactRow>
            </Stack>

            {canChange && choices.length > 0 && (
              <Stack gap={6}>
                <Text fz="xs" fw={600}>
                  The payment it is for
                </Text>
                <Radio.Group value={chosen} onChange={setPicked}>
                  <Table fz="xs" verticalSpacing={6} highlightOnHover>
                    <Table.Tbody>
                      {choices.map((p) => (
                        <Table.Tr
                          key={p.id}
                          onClick={() => setPicked(p.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <Table.Td w={28}>
                            <Radio value={p.id} size="xs" />
                          </Table.Td>
                          <Table.Td>{day(p.collectedAt)}</Table.Td>
                          <Table.Td ta="right" fw={600}>
                            +{money(p.amount, currency)}
                          </Table.Td>
                          <Table.Td c={p.senderName ? undefined : 'dimmed'}>
                            {p.senderName ?? 'unknown sender'}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                      <Table.Tr
                        onClick={() => setPicked(NONE)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Table.Td w={28}>
                          <Radio value={NONE} size="xs" />
                        </Table.Td>
                        <Table.Td colSpan={3} c="dimmed">
                          No payment
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Radio.Group>
                <Group justify="flex-end">
                  <Button
                    size="xs"
                    onClick={submitAssociate}
                    loading={link.isPending}
                    disabled={chosen === current}
                  >
                    Save payment
                  </Button>
                </Group>
              </Stack>
            )}
            {canChange && (
              <Group justify="flex-end">
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  leftSection={<PiTrash size={12} />}
                  onClick={() => {
                    setViewingId(null);
                    setRemoving(viewed);
                  }}
                >
                  Remove
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Modal>

      <Modal
        opened={removing != null}
        onClose={close}
        title="Remove the confirmation"
        size="md"
        centered
        closeOnClickOutside={!busy}
        closeOnEscape={!busy}
        withCloseButton={!busy}
      >
        <Stack gap="md">
          <Text size="sm">
            The confirmation leaves the table. It stays on record with your
            reason; a payment associated with it can then be associated with
            another confirmation. It can never be associated with a payment
            again.
          </Text>
          {removing?.hash && <Code block>{removing.hash}</Code>}
          <Textarea
            label="Reason"
            required
            autosize
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            disabled={busy}
          />
          {error && (
            <Alert color="red">
              <Text size="sm">{error}</Text>
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close} disabled={busy}>
              Keep it
            </Button>
            <Button
              color="red"
              onClick={submitRemove}
              loading={remove.isPending}
              disabled={!reason.trim()}
            >
              Remove confirmation
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
